import View from '@play-co/timestep-core/lib/ui/View';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';
import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { downscale } from 'src/conf/scale';
import { successInviteRewardGems } from 'src/replicant/social/social.ruleset';
import { getRandomStaticShareInviteCreative } from 'src/creatives/share';
import { getCreativeText } from 'src/creatives/text';
import { generateInviteURL, sendInviteURL } from 'src/lib/sharing';
import { track } from 'src/lib/analytics/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { withLoading } from 'src/lib/utils';

type ShareData = {
  inviteUrl: string;
  description: string;
};

/**
 * Show instruction and invite button that can share invite link over native share dialog
 */
export default class PopupInviteURL extends PopupBasicLazy {
  /**
   * Green invite button  shat trigger native share
   */
  private inviteButton: ButtonScaleViewWithText;

  /**
   * Build UI
   */
  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: downscale(1130),
      darkerBg: false,
      tracking: {
        feature: Feature.SHARE_INVITE._,
        $subFeature: Feature.SHARE_INVITE.POPUP,
      },
    });

    this.message.updateOpts({
      size: 28,
      width: 440,
      height: 94,
      x: 50,
      y: 74,
    });

    // Steps info
    new ImageScaleView({
      superview: this.box,
      image: `assets/ui/friends/invite_process_${StateObserver.getLocale()}.png`,
      x: 50,
      y: 180,
      width: downscale(708),
      height: downscale(628),
      scaleMethod: 'contain',
    });

    this.inviteButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      width: downscale(708),
      height: downscale(140),
      x: 50,
      y: 630,
      fontSize: 40,
      font: 'Body',
      labelOffsetX: 15,
      localeText: () => i18n('social.invite.referral.cta'),
      tracking: {
        feature: Feature.SHARE_INVITE._,
        $subFeature: Feature.SHARE_INVITE.POPUP_CTA,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close();
      },
    });

    new ImageView({
      superview: this.inviteButton,
      image: 'assets/ui/friends/copy_link.png',
      width: 46,
      height: 46,
      centerOnOrigin: true,
      y: this.inviteButton.style.height / 2 - 5,
      x: 50,
    });
  }

  /**
   * Preload assets before showing the popup
   */
  public getAssetsGroup() {
    const locale = StateObserver.getLocale();
    return new AssetGroup(
      AssetGroup.constructURLs([
        `assets/ui/friends/invite_process_${locale}.png`,
        `assets/ui/friends/copy_link.png`,
        `assets/ui/friends/ico_link_light.png`,
      ]),
    );
  }

  /**
   * On popup show
   */
  public init() {
    super.init({
      title: i18n('social.invite.referral.title'),
      message: i18n('social.invite.referral.message.gemsAfterJoin', {
        value: successInviteRewardGems,
      }),
    });

    // Make sure that button is on, just in case
    this.inviteButton.setDisabled(false);

    // Start promise for link generation on show, before button clicked
    let linkGeneration = this.generateInviteURL();

    // Setup button click
    this.inviteButton.onClick = async () => {
      // Play click sound effect
      sounds.sfx.play('ok');

      // Disable button after click to prevent multiple clicks
      this.inviteButton.setDisabled(true);

      // Wait for link generation to finished
      // In 99% it will be already finished
      const data = await withLoading(() => linkGeneration, {
        feature: Feature.INVITE._,
        subFeature: Feature.INVITE.LINE_SHARE,
      });

      // Handle generation failure
      if (!data) {
        // Enable button back to try again
        this.inviteButton.setDisabled(false);
        // Restart link generation
        linkGeneration = this.generateInviteURL();
        // Show error popup on failure
        await openPopupPromise('popupError', {
          message: i18n(`error.general.tryAgain`),
        });
        return;
      }

      // Send invite and close popup
      await this.sendInvite(data);

      // Close popup
      this.opts.close();
    };
  }

  /**
   * Generates an invite URL with creative assets and text.
   *
   * This function asynchronously generates an invite URL by fetching a random static share invite creative asset
   * and creative text for the invite. It also includes additional data features for the invite URL.
   *
   * @returns {Promise<ShareData | null>} A promise that resolves to the share data containing the invite URL, or null if generation fails.
   */
  private async generateInviteURL(): Promise<ShareData | null> {
    return generateInviteURL({
      creativeAsset: await getRandomStaticShareInviteCreative(),
      creativeText: getCreativeText('invite'),
      data: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.LINE_SHARE,
      },
    });
  }

  /**
   * Sends an invite URL and tracks the event.
   *
   * @param {ShareData} data - The data required to send the invite, including the invite URL.
   * @returns {Promise<void>} A promise that resolves when the invite has been sent and the event has been tracked.
   *
   * @remarks
   * This function sends an invite URL using the `sendInviteURL` function and retrieves the share method used.
   * It then tracks the invite URL sent event with the share method using the `analytics.pushEvent` function.
   */
  private async sendInvite(data: ShareData): Promise<void> {
    // Send invite URL and retrieve share method used
    const shareMethod = await sendInviteURL({
      ...data,
      fallbackPopupOpts: {
        title: i18n('social.invite.referral.copiedUrl'),
        message: i18n('social.invite.referral.copiedUrlMessage'),
      },
    });

    // Track invite URL sent event with share method
    track('SendInviteURL', {
      url: data.inviteUrl,
      shareMethod,
    });
  }
}
